import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, Mail, Lock, UserCog, Building2, Save, X, AlertCircle } from 'lucide-react';
import { http } from '../../lib/api';

export default function UsuarioForm({ usuario, onGuardado, onCancelar }) {
  const [formData, setFormData] = useState({
    username: '',
    nombre_completo: '',
    email: '',
    password: '',
    rol: 'tecnico',
    activo: true,
    cliente_id: ''
  });
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const esEdicion = !!usuario;

  useEffect(() => {
    cargarClientes();
    if (usuario) {
      setFormData({
        username: usuario.username || '',
        nombre_completo: usuario.nombre_completo || '',
        email: usuario.email || '',
        password: '',
        rol: usuario.rol || 'tecnico',
        activo: usuario.activo !== undefined ? usuario.activo : true,
        cliente_id: usuario.cliente_id || ''
      });
    }
  }, [usuario]);

  const cargarClientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/clientes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(response.data || []);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!formData.username.trim()) {
      nuevosErrores.username = 'El nombre de usuario es requerido';
    } else if (formData.username.length < 3) {
      nuevosErrores.username = 'El nombre de usuario debe tener al menos 3 caracteres';
    }

    if (!formData.nombre_completo.trim()) {
      nuevosErrores.nombre_completo = 'El nombre completo es requerido';
    }

    if (!formData.email.trim()) {
      nuevosErrores.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nuevosErrores.email = 'Formato de email inválido';
    }

    if (!esEdicion && !formData.password.trim()) {
      nuevosErrores.password = 'La contraseña es requerida para usuarios nuevos';
    } else if (formData.password && formData.password.length < 6) {
      nuevosErrores.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (formData.rol === 'cliente' && !formData.cliente_id) {
      nuevosErrores.cliente_id = 'Debe seleccionar un cliente para usuarios con rol cliente';
    }

    setErrors(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleChange = (campo, valor) => {
    setFormData(prev => ({
      ...prev,
      [campo]: valor,
      // Limpiar cliente_id si cambia el rol
      ...(campo === 'rol' && valor !== 'cliente' ? { cliente_id: '' } : {})
    }));

    // Limpiar error del campo al modificarlo
    if (errors[campo]) {
      setErrors(prev => ({ ...prev, [campo]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) {
      return;
    }

    setLoading(true);
    try {
      const datosAEnviar = { ...formData };
      
      // Si no es cliente, limpiar cliente_id
      if (datosAEnviar.rol !== 'cliente') {
        datosAEnviar.cliente_id = null;
      }

      // Si es edición y no se cambió la contraseña, no enviarla
      if (esEdicion && !datosAEnviar.password) {
        delete datosAEnviar.password;
      }

      const token = localStorage.getItem('token');
      
      if (esEdicion) {
        await http.put(`/usuarios/${usuario._id}`, datosAEnviar, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await http.post('/usuarios', datosAEnviar, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onGuardado();
    } catch (error) {
      console.error('Error guardando usuario:', error);
      const mensajeError = error.response?.data?.detail || error.message || 'Error desconocido';
      alert(`Error al guardar usuario: ${mensajeError}`);
    } finally {
      setLoading(false);
    }
  };

  const getRolBadge = (rol) => {
    const configs = {
      admin: { bg: 'bg-red-100', text: 'text-red-800', label: 'Administrador' },
      tecnico: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Técnico' },
      cliente: { bg: 'bg-green-100', text: 'text-green-800', label: 'Cliente' }
    };
    const config = configs[rol] || configs.tecnico;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                <UserCog className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {esEdicion ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h2>
                <p className="text-emerald-100 text-sm">
                  {esEdicion ? 'Modifica los datos del usuario' : 'Completa los datos para crear un nuevo usuario'}
                </p>
              </div>
            </div>
            <button
              onClick={onCancelar}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline h-4 w-4 mr-1" />
                Nombre de Usuario
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                  errors.username ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="ej. jperez"
                disabled={esEdicion} // No permitir cambiar username en edición
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.username}
                </p>
              )}
            </div>

            {/* Nombre completo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline h-4 w-4 mr-1" />
                Nombre Completo
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={formData.nombre_completo}
                onChange={(e) => handleChange('nombre_completo', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                  errors.nombre_completo ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="ej. Juan Pérez González"
              />
              {errors.nombre_completo && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.nombre_completo}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline h-4 w-4 mr-1" />
                Email
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                  errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="ej. juan.perez@empresa.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Contraseña */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="inline h-4 w-4 mr-1" />
                Contraseña
                {!esEdicion && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                  errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder={esEdicion ? "Dejar vacío para no cambiar" : "Mínimo 6 caracteres"}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.password}
                </p>
              )}
              {esEdicion && (
                <p className="mt-1 text-xs text-gray-500">
                  Dejar vacío si no desea cambiar la contraseña actual
                </p>
              )}
            </div>
          </div>

          {/* Rol y cliente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Rol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserCog className="inline h-4 w-4 mr-1" />
                Rol
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                value={formData.rol}
                onChange={(e) => handleChange('rol', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
              >
                <option value="admin">Administrador</option>
                <option value="tecnico">Técnico</option>
                <option value="cliente">Cliente</option>
              </select>
              <div className="mt-2 flex items-center justify-between">
                {getRolBadge(formData.rol)}
                
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => handleChange('activo', e.target.checked)}
                    className="rounded border-gray-300 text-emerald-600 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50"
                  />
                  <span className="text-sm text-gray-700">Usuario Activo</span>
                </label>
              </div>
            </div>

            {/* Cliente (solo si rol es cliente) */}
            {formData.rol === 'cliente' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="inline h-4 w-4 mr-1" />
                  Cliente Asociado
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  value={formData.cliente_id}
                  onChange={(e) => handleChange('cliente_id', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                    errors.cliente_id ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                >
                  <option value="">-- Seleccionar cliente --</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id || cliente._id} value={cliente.id || cliente._id}>
                      {cliente.nombre} ({cliente.tipo})
                    </option>
                  ))}
                </select>
                {errors.cliente_id && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.cliente_id}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Este usuario solo podrá ver información del cliente seleccionado
                </p>
                {clientes.length === 0 && (
                  <p className="mt-1 text-xs text-orange-600">
                    No hay clientes registrados. Cree un cliente primero.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Información sobre permisos según rol */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Permisos del rol seleccionado:</h4>
            <div className="text-sm text-gray-600">
              {formData.rol === 'admin' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>Acceso completo al sistema</li>
                  <li>Gestión de usuarios, clientes y configuración</li>
                  <li>Visualización de todas las órdenes y reportes</li>
                </ul>
              )}
              {formData.rol === 'tecnico' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>Gestión de órdenes de servicio</li>
                  <li>Visualización de equipos y agendamientos</li>
                  <li>Generación de reportes básicos</li>
                </ul>
              )}
              {formData.rol === 'cliente' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>Solo visualización de sus propias órdenes</li>
                  <li>Acceso a sus equipos registrados</li>
                  <li>Consulta de agendamientos programados</li>
                </ul>
              )}
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancelar}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{esEdicion ? 'Actualizar' : 'Crear Usuario'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}